import React from 'react';
import { Card, Statistic, Progress, Tag, Button, Row, Col } from 'antd';
import { BookOutlined, BulbOutlined, CalculatorOutlined } from '@ant-design/icons';
import RadarChart from './RadarChart';
import { 
  ReadingAssessment,
  ThinkingAssessment,
  MathematicalAssessment
} from '../../types/assessment';
import {
  READING_DIMENSIONS,
  THINKING_DIMENSIONS,
  MATHEMATICAL_DIMENSIONS,
  getAssessmentLevel,
  extractDimensionScores
} from '../../utils/assessmentUtils';

interface AssessmentResultCardProps {
  type: 'reading' | 'thinking' | 'mathematics';
  data: ReadingAssessment | ThinkingAssessment | MathematicalAssessment;
  overallScore: number;
  showRadarChart?: boolean;
  size?: 'small' | 'medium' | 'large';
  onViewDetails?: () => void;
  onRetakeTest?: () => void;
}

const AssessmentResultCard: React.FC<AssessmentResultCardProps> = ({
  type,
  data,
  overallScore,
  showRadarChart = true,
  size = 'medium',
  onViewDetails,
  onRetakeTest,
}) => {
  const getTypeConfig = () => {
    switch (type) {
      case 'reading':
        return {
          title: 'Reading Comprehension',
          icon: <BookOutlined style={{ marginRight: '8px', color: '#52c41a' }} />,
          dimensions: READING_DIMENSIONS,
          color: '#52c41a'
        };
      case 'thinking':
        return {
          title: 'Thinking Skills',
          icon: <BulbOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />,
          dimensions: THINKING_DIMENSIONS,
          color: '#fa8c16'
        };
      case 'mathematics':
        return {
          title: 'Mathematical Reasoning',
          icon: <CalculatorOutlined style={{ marginRight: '8px', color: '#1890ff' }} />,
          dimensions: MATHEMATICAL_DIMENSIONS,
          color: '#1890ff'
        };
    }
  };

  const config = getTypeConfig();
  const level = getAssessmentLevel(overallScore);

  const getProgressColor = (score: number) => {
    if (score >= 9) return '#52c41a';
    if (score >= 7) return '#1890ff';
    if (score >= 4) return '#faad14';
    return '#f5222d';
  };

  const cardHeight = showRadarChart ? (size === 'large' ? '600px' : size === 'medium' ? '480px' : '400px') : 'auto';

  return (
    <Card
      title={
        <span>
          {config.icon}
          {config.title}
        </span>
      }
      extra={
        <div style={{ display: 'flex', gap: '8px' }}>
          {onViewDetails && (
            <Button type="link" onClick={onViewDetails} style={{ padding: 0 }}>
              View Details
            </Button>
          )}
          {onRetakeTest && (
            <Button type="primary" size="small" onClick={onRetakeTest}>
              Retake
            </Button>
          )}
        </div>
      }
      style={{ 
        height: cardHeight,
        display: 'flex',
        flexDirection: 'column'
      }}
      bodyStyle={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: showRadarChart ? '16px' : '24px'
      }}
    >
      {/* Score Summary */}
      <div style={{ marginBottom: showRadarChart ? '16px' : '24px' }}>
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Statistic
              title="Overall Score"
              value={overallScore}
              suffix="/ 10"
              precision={1}
              valueStyle={{ 
                color: getProgressColor(overallScore),
                fontSize: size === 'large' ? '32px' : '24px'
              }}
            />
            <Progress 
              percent={overallScore * 10}
              strokeColor={getProgressColor(overallScore)}
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Tag 
              color={level.color}
              style={{ 
                fontSize: '14px', 
                padding: '4px 12px',
                marginBottom: '8px'
              }}
            >
              {level.label}
            </Tag>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              lineHeight: '1.4'
            }}>
              {level.description}
            </div>
          </Col>
        </Row>
      </div>

      {/* Radar Chart */}
      {showRadarChart && (
        <div style={{ 
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: size === 'large' ? '450px' : '350px',
          paddingTop: '20px'
        }}>
          <RadarChart
            title=""
            data={extractDimensionScores(data, config.dimensions)}
            dimensions={config.dimensions}
            size={size === 'large' ? 'medium' : 'small'}
            showLegend={size !== 'small'}
          />
        </div>
      )}

      {/* Detailed Scores (when no radar chart) */}
      {!showRadarChart && (
        <div style={{ flex: 1 }}>
          <h4 style={{ marginBottom: '16px', color: '#1f2937' }}>Detailed Breakdown:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {config.dimensions.map(dimension => {
              const score = (data as any)[dimension.key] || 0;
              return (
                <div key={dimension.key} style={{
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${getProgressColor(score)}`
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: '500' }}>
                      {dimension.label}
                    </span>
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: 'bold',
                      color: getProgressColor(score)
                    }}>
                      {score.toFixed(1)}
                    </span>
                  </div>
                  <Progress 
                    percent={score * 10}
                    strokeColor={getProgressColor(score)}
                    showInfo={false}
                    size="small"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OC Readiness Indicator */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px',
        background: level.color + '10',
        borderRadius: '6px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
          OC Exam Readiness
        </div>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: level.color }}>
          {level.ocImplication}
        </div>
      </div>
    </Card>
  );
};

export default AssessmentResultCard;